import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getInfiniteQueryListNextParam } from '@/utils/list';

import {
  createDigitalNavigationLog,
  deleteDigitalNavigationLog,
  getDigitalNavigationLogById,
  getDigitalNavigationLogByDate,
  getDigitalNavigationLogPdf,
  getDigitalNavigationLogs,
  updateDigitalNavigationLog,
} from './digital-navigation-log.api';
import type {
  CreateDigitalNavigationLogFormValues,
  UpdateDigitalNavigationLogFormValues,
} from './digital-navigation-log.dto';

const ROOT_KEY = 'digital-navigation-logs';

export const useDigitalNavigationLogsQuery = (limit: number) =>
  useInfiniteQuery({
    queryKey: [ROOT_KEY, limit],
    queryFn: ({ pageParam }) => getDigitalNavigationLogs(limit, pageParam),
    initialPageParam: 1,
    getNextPageParam: getInfiniteQueryListNextParam,
  });

export const useDigitalNavigationLogByIdQuery = (id: string | null | undefined) =>
  useQuery({
    queryKey: [ROOT_KEY, 'by-id', id],
    queryFn: () => getDigitalNavigationLogById(id!),
    enabled: !!id,
  });

export const useDigitalNavigationLogByDateQuery = (date: string | null | undefined) =>
  useQuery({
    queryKey: [ROOT_KEY, 'by-date', date],
    queryFn: () => getDigitalNavigationLogByDate(date!),
    enabled: !!date,
  });

export const useCreateDigitalNavigationLogMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: CreateDigitalNavigationLogFormValues) =>
      createDigitalNavigationLog(values),
    onSuccess(_, { date }) {
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY] });
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'by-date', date] });
    },
  });
};

export const useUpdateDigitalNavigationLogMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: UpdateDigitalNavigationLogFormValues) =>
      updateDigitalNavigationLog(values),
    onSuccess(_, { id, date }) {
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY] });
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'by-id', id] });
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'by-date', date] });
    },
  });
};

export const useDeleteDigitalNavigationLogMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => deleteDigitalNavigationLog(id),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY] });
    },
  });
};

export const useDigitalNavigationLogPdfMutation = () =>
  useMutation({
    mutationFn: ({ id, timezone }: { id: string; timezone?: string }) =>
      getDigitalNavigationLogPdf(id, timezone),
  });
